import React, { useState, useEffect } from "react";
import axios from "axios";
import { AesEncrypt, AesDecrypt } from "../../../services/crypto";
import WebService from "../../../services/webservice";
import Swal from 'sweetalert2';
import { Modal, Button, Form } from 'react-bootstrap';
import moment from 'moment';
import { ChevronUp, ChevronDown } from 'lucide-react';
import '../ProfileQuestions/ProfileQuestions.css';
import './SystemConfig.css';

const SystemConfig = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Filters
    const [searchTerm, setSearchTerm] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // Pagination & Sorting
    const [totalCount, setTotalCount] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortBy, setSortBy] = useState('latest');
    const [sortOrder, setSortOrder] = useState(-1);

    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({ fieldName: '', value: '' });
    const [errors, setErrors] = useState({});

    const fetchData = async () => {
        setLoading(true);
        try {
            const payload = {
                limit: itemsPerPage,
                page: currentPage,
                search: searchTerm,
                startDate: startDate,
                endDate: endDate,
                sortBy: sortBy,
                sortOrder: sortOrder
            };
            const encryptedPayload = AesEncrypt(payload);
            const response = await axios.post(`${WebService}api/get_all_system_config`, { EncData: encryptedPayload });
            if (response.data.response === 'S') {
                const decrypted = AesDecrypt(response.data.data);
                setData(decrypted?.data || []);
                setTotalCount(decrypted?.totalCount || 0);
            } else {
                setData([]);
                setTotalCount(0);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchData();
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm, startDate, endDate, currentPage, itemsPerPage, sortBy, sortOrder]);

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortOrder(sortOrder === -1 ? 1 : -1);
        } else {
            setSortBy(field);
            setSortOrder(1);
        }
    };

    const SortIcon = ({ field }) => {
        if (sortBy !== field) return <ChevronDown size={14} className="text-muted" />;
        return sortOrder === 1 ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        });
        if (!result.isConfirmed) return;
        try {
            const response = await axios.post(`${WebService}api/delete_system_config`, { EncData: AesEncrypt({ _id: id }) });
            if (response.data.response === 'S') {
                Swal.fire('Deleted!', 'Record deleted successfully.', 'success');
                fetchData();
            } else {
                Swal.fire('Error', response.data.message, 'error');
            }
        } catch (err) {
            Swal.fire('Error', 'Failed to delete record', 'error');
        }
    };

    const openAddModal = () => {
        setEditingItem(null);
        setFormData({ fieldName: '', value: '' });
        setErrors({});
        setShowModal(true);
    };

    const openEditModal = (item) => {
        setEditingItem(item);
        setFormData({ fieldName: item.fieldName || '', value: item.value || '' });
        setErrors({});
        setShowModal(true);
    };

    const handleCloseModal = async () => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "Any unsaved changes will be lost.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, close it!',
            cancelButtonText: 'No, keep it',
            customClass: { container: 'my-swal-container' }
        });
        if (result.isConfirmed) {
            setShowModal(false);
        }
    };

    const handleSave = async () => {
        const newErrors = {};
        if (!formData.fieldName.trim()) newErrors.fieldName = 'Field Name is required';
        if (!formData.value.trim()) newErrors.value = 'Value is required';

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        const result = await Swal.fire({
            title: 'Confirm Save',
            text: "Do you want to save these changes?",
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, save it!',
            customClass: { container: 'my-swal-container' }
        });

        if (!result.isConfirmed) return;

        try {
            const userId = localStorage.getItem('id') || localStorage.getItem('adminId') || '';
            const payload = { ...formData, userId };
            if (editingItem) {
                payload._id = editingItem._id;
            }

            const encryptedPayload = AesEncrypt(payload);
            const endpoint = editingItem ? 'edit_system_config' : 'add_system_config';
            
            const response = await axios.post(`${WebService}api/${endpoint}`, { EncData: encryptedPayload });
            
            if (response.data.response === 'S') {
                Swal.fire({ title: 'Saved!', text: 'The configuration has been saved.', icon: 'success', customClass: { container: 'my-swal-container' } });
                setShowModal(false);
                fetchData();
            } else {
                Swal.fire({ title: 'Error', text: response.data.message, icon: 'error', customClass: { container: 'my-swal-container' } });
            }
        } catch (err) {
            console.error(err);
            Swal.fire({ title: 'Error', text: 'Failed to save record', icon: 'error', customClass: { container: 'my-swal-container' } });
        }
    };

    const totalPages = Math.ceil(totalCount / itemsPerPage);

    return (
        <div className="container-fluid" style={{ padding: '20px' }}>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="mb-0" style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>System Configurations</h4>
                <button className="btn btn-primary" style={{ width: '150px', maxWidth: '150px', minWidth: '150px', flex: '0 0 auto', padding: '10px 20px', backgroundColor: '#8b5cf6', borderColor: '#8b5cf6', borderRadius: '8px', fontWeight: '600', letterSpacing: '0.5px' }} onClick={openAddModal}>+ ADD</button>
            </div>

            <div className="card mb-4" style={{ borderRadius: '12px', border: 'none', boxShadow: '0 2px 10px rgba(0,0,0,0.02)' }}>
                <div className="card-body py-3">
                    <div className="row g-3 align-items-center">
                        <div className="col-md-4">
                            <label className="form-label mb-1" style={{ fontSize: '13px', color: '#64748b' }}>Search</label>
                            <input type="text" className="form-control" style={{ padding: '0.4rem 0.75rem', height: '38px', borderRadius: '8px', border: '1px solid #e2e8f0' }} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search Field Name, Value, Created By..." />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label mb-1" style={{ fontSize: '13px', color: '#64748b' }}>Start Date</label>
                            <input type="date" className="form-control" style={{ padding: '0.4rem 0.75rem', height: '38px', borderRadius: '8px', border: '1px solid #e2e8f0' }} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label mb-1" style={{ fontSize: '13px', color: '#64748b' }}>End Date</label>
                            <input type="date" className="form-control" style={{ padding: '0.4rem 0.75rem', height: '38px', borderRadius: '8px', border: '1px solid #e2e8f0' }} value={endDate} onChange={(e) => setEndDate(e.target.value)} min={startDate} />
                        </div>
                        {(searchTerm !== '' || startDate !== '' || endDate !== '') && (
                            <div className="col-md-2 d-flex align-items-end" style={{ paddingBottom: '1px' }}>
                                <button
                                    className="btn btn-danger text-white w-100"
                                    style={{ height: '38px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    onClick={() => {
                                        setSearchTerm('');
                                        setStartDate('');
                                        setEndDate('');
                                    }}
                                >
                                    <i className="fa-solid fa-xmark me-1"></i> Clear
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="pq-table-wrapper">
                <div className="pq-table-scroll">
                    <table className="pq-table">
                        <thead>
                            <tr>
                                <th style={{ textAlign: 'center' }}>S.NO</th>
                                <th onClick={() => handleSort('fieldName')} style={{ cursor: 'pointer' }}>FIELD NAME (KEY) <SortIcon field="fieldName" /></th>
                                <th onClick={() => handleSort('value')} style={{ cursor: 'pointer' }}>VALUE <SortIcon field="value" /></th>
                                <th onClick={() => handleSort('latest')} style={{ cursor: 'pointer' }}>LAST UPDATED DATE <SortIcon field="latest" /></th>
                                <th onClick={() => handleSort('createdBy')} style={{ cursor: 'pointer' }}>CREATED BY <SortIcon field="createdBy" /></th>
                                <th style={{ textAlign: 'center' }}>ACTIONS</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="6">
                                        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '32vh' }}>
                                            Loading...
                                        </div>
                                    </td>
                                </tr>
                            ) : data.length === 0 ? (
                                <tr>
                                    <td colSpan="6">
                                        <div className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: '32vh' }}>
                                            <h5 style={{ color: '#6e7480', fontWeight: 'bold' }}>No Record Found</h5>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                data.map((item, index) => (
                                    <tr key={item._id}>
                                        <td style={{ textAlign: 'center' }}>
                                            {((currentPage - 1) * itemsPerPage) + index + 1}
                                        </td>
                                        <td style={{ fontWeight: '500' }}>{item.fieldName}</td>
                                        <td>{item.value}</td>
                                        <td>{moment(item.updatedAt || item.createdAt).format('DD MMM YYYY, hh:mm A')}</td>
                                        <td>{item.createdBy || 'Admin'}</td>
                                        <td>
                                            <div className="d-flex justify-content-center gap-2">
                                                <button className="pq-action-btn" onClick={() => openEditModal(item)} title="Edit">
                                                    <i className="fas fa-pen"></i>
                                                </button>
                                                <button className="pq-action-btn pq-action-btn-danger" onClick={() => handleDelete(item._id)} title="Delete">
                                                    <i className="fa fa-trash"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Footer */}
                <div className="pq-pagination-container d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                        <span className="pq-pagination-text me-2">Showing {totalCount > 0 ? ((currentPage - 1) * itemsPerPage) + 1 : 0} – {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} entries. Per page: </span>
                        <select className="form-select form-select-sm pq-filter-input" value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }} style={{ width: '70px', display: 'inline-block', height: '32px !important', padding: '0 10px !important' }}>
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                        </select>
                    </div>

                    <ul className="pagination pagination-sm mb-0">
                        <li className={`page-item pq-page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button className="page-link pq-page-link" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
                                &laquo;
                            </button>
                        </li>
                        {[...Array(totalPages || 0)].map((_, i) => {
                            if (i === 0 || i === (totalPages || 1) - 1 || (i >= currentPage - 2 && i <= currentPage)) {
                                return (
                                    <li className={`page-item pq-page-item ${i + 1 === currentPage ? 'active' : ''}`} key={i}>
                                        <button className="page-link pq-page-link" onClick={() => handlePageChange(i + 1)}>
                                            {i + 1}
                                        </button>
                                    </li>
                                );
                            }
                            if (i === 1 && currentPage > 3) return <li className="page-item pq-page-item disabled" key="start-ellipsis"><span className="page-link pq-page-link">...</span></li>;
                            if (i === (totalPages || 1) - 2 && currentPage < (totalPages || 1) - 2) return <li className="page-item pq-page-item disabled" key="end-ellipsis"><span className="page-link pq-page-link">...</span></li>;
                            return null;
                        })}
                        <li className={`page-item pq-page-item ${currentPage === totalPages || totalPages === 0 ? 'disabled' : ''}`}>
                            <button className="page-link pq-page-link" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages || totalPages === 0}>
                                &raquo;
                            </button>
                        </li>
                    </ul>
                </div>
            </div>

            <Modal show={showModal} onHide={handleCloseModal} centered backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>{editingItem ? 'Edit Configuration' : 'Add Configuration'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Field Name (Key) <span className="text-danger">*</span></Form.Label>
                            <Form.Control 
                                type="text" 
                                placeholder="Enter Field Name (e.g., API_URL)" 
                                value={formData.fieldName}
                                onChange={(e) => setFormData({...formData, fieldName: e.target.value})}
                                isInvalid={!!errors.fieldName}
                            />
                            <Form.Control.Feedback type="invalid">{errors.fieldName}</Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Value <span className="text-danger">*</span></Form.Label>
                            <Form.Control 
                                type="text" 
                                placeholder="Enter Value" 
                                value={formData.value}
                                onChange={(e) => setFormData({...formData, value: e.target.value})}
                                isInvalid={!!errors.value}
                            />
                            <Form.Control.Feedback type="invalid">{errors.value}</Form.Control.Feedback>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModal}>
                        Cancel
                    </Button>
                    <Button variant="primary" style={{ backgroundColor: '#8b5cf6', borderColor: '#8b5cf6' }} onClick={handleSave}>
                        Save
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default SystemConfig;
